import { ApolloClient } from "apollo-client";
import {
  InMemoryCache,
  defaultDataIdFromObject,
  IntrospectionFragmentMatcher,
} from "apollo-cache-inmemory";
import { ApolloLink } from "apollo-link";
import { toIdValue } from "apollo-utilities";
import { RetryLink } from "apollo-link-retry";
import makeDeprecationLink from "./linkFactories/makeDeprecationLink";
import makePersistedQueriesLink from "./linkFactories/makePersistedQueriesLink";
import makeCacheControlLink from "./linkFactories/makeCacheControlLink";
import makeErrorLink from "./linkFactories/makeErrorLink";
import { collectStorefrontContentMetadataLink } from "theme/modules/StorefrontContent/StorefrontContentMetadataProvider";

const introspectionQueryResultData = require(".front-commerce/cache/fragmentTypes.json"); // eslint-disable-line import/no-commonjs

const dataIdFromObject = (object) => {
  if (object.__typename) {
    if (object.sku && object.__typename === "Product") {
      return `${object.__typename}_${object.sku}`;
    }
    if (object.item_id && object.__typename === "CartItem") {
      return `${object.__typename}_${object.item_id}`;
    }
    if (object.identifier && object.__typename === "Entity") {
      return `${object.__typename}_${object.identifier}`;
    }
    if (object.order_id && object.__typename === "Order") {
      return `${object.__typename}_${object.order_id}`;
    }
  }
  return defaultDataIdFromObject(object);
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const makeApolloClient = (options = {}) => {
  const { ssr = false, link, onMaintenanceError = () => {} } = options;

  const cache = new InMemoryCache({
    dataIdFromObject,
    addTypename: true,
    fragmentMatcher,
    cacheRedirects: {
      Query: {
        product: (_, { sku }) => {
          return toIdValue(
            cache.config.dataIdFromObject({
              __typename: "ProductType",
              sku,
            })
          );
        },
      },
    },
  });

  return new ApolloClient({
    link: ApolloLink.from(
      [
        ssr ? null : collectStorefrontContentMetadataLink,
        makeDeprecationLink(),
        makeCacheControlLink({ ssr }),
        makeErrorLink({ onMaintenanceError }),
        new RetryLink(),
        makePersistedQueriesLink(), // keep this before the http link (check https://www.apollographql.com/docs/apollo-server/performance/apq/#step-2-enable-automatic-persisted-queries)
        link,
      ].filter(Boolean)
    ),
    cache: ssr ? cache : cache.restore(window.__APOLLO_STATE__),
    ssrMode: ssr,
    ssrForceFetchDelay: 100,
    queryDeduplication: true,
  });
};

export default makeApolloClient;
